import { watchEffect } from 'vue'
import type { Pinia } from 'pinia'
import { useAudienceStore } from '@/app/stores/audience'
import { fetchVerifiedJson, VerifiedFetchError } from '@/core/release/verifiedFetch'
import { audienceManifestUrl } from '@/core/runtime/environment'

export type ManifestProbeDecision = 'unchanged' | 'reload' | 'ignore'

export interface ActiveRuntimeIdentity {
  stadiumId: string
  eventId: string
  checksum: string
  versionNo?: string
}

export function installAudienceManifestProbe(pinia: Pinia): () => void {
  const audience = useAudienceStore(pinia)
  let running = false
  return watchEffect((onCleanup) => {
    if (audience.status !== 'ready' || audience.previewMode) return
    const identity = activeRuntimeIdentity(audience)
    if (!identity) return
    const timer = window.setInterval(async () => {
      if (running) return
      running = true
      try {
        if (await probeAudienceManifest(identity) === 'reload') window.location.reload()
      } finally {
        running = false
      }
    }, 60_000)
    onCleanup(() => window.clearInterval(timer))
  })
}

export async function probeAudienceManifest(
  identity: ActiveRuntimeIdentity,
  fetcher: typeof fetch = fetch,
): Promise<ManifestProbeDecision> {
  try {
    const manifest = await fetchVerifiedJson(
      audienceManifestUrl(identity.stadiumId),
      { cache: 'no-cache' },
      fetcher,
    )
    return decideManifestProbe(manifest, identity)
  } catch (error) {
    if (error instanceof VerifiedFetchError && error.code === 'HTTP' && error.status === 404) return 'reload'
    return 'ignore'
  }
}

export function decideManifestProbe(value: unknown, identity: ActiveRuntimeIdentity): ManifestProbeDecision {
  if (!isRecord(value)) return 'ignore'
  if (value.schemaVersion === 'venue-audience-manifest/v1') {
    if (value.stadiumId !== identity.stadiumId || value.state !== 'active') return 'reload'
    if (String(value.eventId || '') !== identity.eventId) return 'reload'
    return String(value.audienceReleaseChecksum || '') === identity.checksum ? 'unchanged' : 'reload'
  }
  const eventId = String(value.eventId || '')
  const checksum = String(value.checksum || '')
  if (!eventId || !checksum) return 'reload'
  const versionNo = String(value.versionNo || '')
  const sameVersion = !identity.versionNo || versionNo === identity.versionNo
  return eventId === identity.eventId && checksum === identity.checksum && sameVersion ? 'unchanged' : 'reload'
}

function activeRuntimeIdentity(audience: ReturnType<typeof useAudienceStore>): ActiveRuntimeIdentity | null {
  const eventId = String(audience.config?._runtime?.eventId || '')
  const checksum = String(audience.config?._runtime?.checksum || '')
  const versionNo = String(audience.config?._runtime?.versionNo || '')
  return audience.stadiumId && eventId && checksum ? { stadiumId: audience.stadiumId, eventId, checksum, versionNo: versionNo || undefined } : null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
